import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  inject,
  OnInit,
  viewChild,
} from "@angular/core";
import { FormBuilder, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { ActivatedRoute, Router } from "@angular/router";
import { finalize, first } from "rxjs/operators";
import {
  CONFIRM_DIALOG_CONFIRM_ACTION,
  CONFIRM_DIALOG_PRODUCT_CATEGORY_ATTRIB_DELETE,
  ProductCategoriesAttributes,
} from "../../locale/consts.en";
import { ItemEditComponent } from "../item-edit/item-edit.component";
import { MasterDataService } from "../master-data/master-data-service";
import {
  ProductCategory,
  ProductCategoryAttribute,
} from "../master-data/master-data.model";
import {
  ConfDialogType,
  ConfirmDialogModel,
} from "../shared/confirm-dialog/confim-dialog.model";
import { ConfirmDialogComponent } from "../shared/confirm-dialog/confirm-dialog.component";
import { sortByStringWithEmptyAtEnd } from "../shared/helpers/helper-functions";
import { ProductCategoriesAttributeEditComponent } from "./product-categories-attribute-edit/product-categories-attribute-edit.component";

@Component({
  selector: "app-product-categories",
  templateUrl: "./product-categories.component.html",
  styleUrls: ["./product-categories.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ProductCategoriesComponent implements OnInit, AfterViewInit {
  private dialog = inject(MatDialog);
  private masterDataService = inject(MasterDataService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);

  readonly sort = viewChild(MatSort);
  dataSource: MatTableDataSource<ProductCategory>;
  displayedColumns: string[] = [
    "name",
    "externalId",
    "attributes",
    "add",
    "edit",
  ];
  categories: ProductCategory[] = [];
  ProductCategoriesAtt = ProductCategoriesAttributes;
  categoriesForm = this.fb.group({
    name: [null, [Validators.required]],
    externalId: [null],
  });

  ngOnInit(): void {
    const categories = this.route.snapshot.data.categories ?? [];
    this.categories = sortByStringWithEmptyAtEnd(
      categories,
      (item) => item.name,
    );
    // this.route.snapshot.data.categories.sort((a, b) =>
    //   a.name.localeCompare(b.name)
    // );
    this.dataSource = new MatTableDataSource(this.categories);
  }

  saveCategoryToApi(category: ProductCategory) {
    this.masterDataService
      .updateProductCategory(category)
      .pipe(
        finalize(() => {
          this.masterDataService
            .getProductCategories()
            .pipe(first())
            .subscribe((categories) => {
              this.categories = categories;
              this.dataSource.data = this.categories;
            });
        }),
      )
      .subscribe({
        next: (response) => {
          this.masterDataService.handleResponse(response);
        },
        error: () => this.masterDataService.savingErrorMessage(),
      });
  }

  updateCategory(t: ProductCategory) {
    const category = new ProductCategory();
    Object.assign(category, t);
    const prodCategoryAttribs: ProductCategoryAttribute[] =
      category.productCategoryAttributes != null
        ? category.productCategoryAttributes
        : [];
    const dialogRef = this.dialog.open(ItemEditComponent, {
      width: "20vw",
      data: category,
    });
    dialogRef.afterClosed().subscribe(async (data: ProductCategory) => {
      if (data != null) {
        data.productCategoryAttributes = prodCategoryAttribs;
        // console.log(data);
        this.saveCategoryToApi(data);
      }
    });
  }

  ngAfterViewInit() {
    this.dataSource.sort = this.sort();
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.dataSource.filter = filterValue.trim().toLowerCase();
  }

  onAddCategory() {
    const category = new ProductCategory();
    const dialogRef = this.dialog.open(ItemEditComponent, {
      width: "20vw",
      data: category,
    });
    dialogRef.afterClosed().subscribe(async (data: ProductCategory) => {
      if (data != null) {
        this.saveCategoryToApi(data);
      }
    });
  }

  onBack() {
    this.router.navigate(["/"]).then();
  }

  deleteProductCategoryAtrribute(
    productCategory: ProductCategory,
    i: number,
  ): void {
    const dialogData = new ConfirmDialogModel(
      CONFIRM_DIALOG_CONFIRM_ACTION + "  " + productCategory.name,
      CONFIRM_DIALOG_PRODUCT_CATEGORY_ATTRIB_DELETE +
        "   " +
        productCategory.productCategoryAttributes[i].name,
      ConfDialogType.CONFIRM,
    );

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      maxWidth: "400px",
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (data) {
        productCategory.productCategoryAttributes.splice(i, 1);
        console.log("Deleting product category attribute", productCategory);
        this.saveCategoryToApi(productCategory);
      }
    });
  }

  addProductCategory(category: ProductCategory) {
    const prodCategoryAttribs: ProductCategoryAttribute[] =
      category.productCategoryAttributes != null
        ? category.productCategoryAttributes
        : [];
    const dialogRef = this.dialog.open(
      ProductCategoriesAttributeEditComponent,
      {
        width: "20vw",
      },
    );
    dialogRef.afterClosed().subscribe((data: ProductCategoryAttribute) => {
      if (data != null) {
        prodCategoryAttribs.push(data);
        category.productCategoryAttributes = prodCategoryAttribs;
        this.saveCategoryToApi(category);
      }
    });
  }
}
